import { getRuntimeConfig } from '../config/runtimeConfig';

/**
 * Istoricul măsurătorilor. Un punct = un interval agregat de backend; câmpurile
 * lipsă înseamnă „senzorul n-a raportat metrica în intervalul respectiv”.
 */
export interface HistoryPoint {
  /** ISO 8601, începutul intervalului agregat. */
  t: string;
  temperature?: number | null;
  humidity?: number | null;
  pressure?: number | null;
  wind_speed?: number | null;
  wind_direction?: number | null;
  precipitation?: number | null;
  uv_index?: number | null;
  illuminance?: number | null;
  battery?: number | null;
}

/** Metricile pentru care există serie de istoric. */
export type MetricKey =
  | 'temperature'
  | 'humidity'
  | 'pressure'
  | 'wind_speed'
  | 'wind_direction'
  | 'precipitation'
  | 'uv_index'
  | 'illuminance'
  | 'battery';

export const METRIC_KEYS: MetricKey[] = [
  'temperature',
  'humidity',
  'pressure',
  'wind_speed',
  'wind_direction',
  'precipitation',
  'uv_index',
  'illuminance',
  'battery',
];

export type RangeKey = '24h' | '7d' | '30d' | '1y';

export interface HistoryRange {
  key: RangeKey;
  /** Text scurt pentru selector. */
  short: string;
  /** Text lung pentru antetul vederii de detaliu. */
  label: string;
  hours: number;
  /** Granularitatea cerută backendului; el decide agregarea. */
  bucket: string;
}

/**
 * Intervalele oferite în interfață. Adăugarea unuia nou nu cere modificări în
 * altă parte — selectorul și fetch-ul citesc din lista asta.
 *
 * `1d` și `1mo` sunt calendaristice: backendul le taie la miezul nopții locale
 * și la întâi ale lunii, nu la fiecare 86.400 de secunde.
 */
export const HISTORY_RANGES: HistoryRange[] = [
  { key: '24h', short: '24 h', label: 'ultimele 24 de ore', hours: 24, bucket: '10m' },
  { key: '7d', short: 'săptămână', label: 'ultima săptămână', hours: 24 * 7, bucket: '1h' },
  { key: '30d', short: 'lună', label: 'ultima lună', hours: 24 * 30, bucket: '1d' },
  { key: '1y', short: 'an', label: 'ultimul an', hours: 24 * 365, bucket: '1mo' },
];

export const DEFAULT_RANGE = HISTORY_RANGES[0];

export function findRange(key: RangeKey): HistoryRange {
  return HISTORY_RANGES.find((range) => range.key === key) ?? DEFAULT_RANGE;
}

/** Fereastra efectiv desenată. Un interval predefinit sau o zonă selectată. */
export interface HistoryWindow {
  /** Milisecunde epoch. */
  from: number;
  to: number;
  bucket: string;
  /** Ce scrie în antet. */
  label: string;
}

export function windowFromRange(range: HistoryRange, now = Date.now()): HistoryWindow {
  return {
    from: now - range.hours * 3_600_000,
    to: now,
    bucket: range.bucket,
    label: range.label,
  };
}

/**
 * Granularitatea potrivită pentru o zonă selectată.
 *
 * Coborârea se oprește la un minut: sub el, o valoare pe punct înseamnă deja
 * fiecare schimbare raportată, iar agregarea n-ar mai avea ce comprima.
 */
export function bucketForSpan(spanMs: number): string {
  const hours = spanMs / 3_600_000;
  if (hours <= 3) return '1m';
  if (hours <= 12) return '5m';
  if (hours <= 48) return '10m';
  if (hours <= 24 * 8) return '1h';
  if (hours <= 24 * 45) return '1d';
  return '1mo';
}

/** Statistici pe citirile brute, întoarse de `GET /history/summary`. */
export interface MetricSummary {
  min: number | null;
  max: number | null;
  avg: number | null;
  last: number | null;
  count: number;
}

export interface HistorySummary {
  from: string;
  to: string;
  readings: number;
  metrics: Partial<Record<MetricKey, MetricSummary>>;
}

function isHistoryPoint(value: unknown): value is HistoryPoint {
  return (
    typeof value === 'object' && value !== null && typeof (value as HistoryPoint).t === 'string'
  );
}

/**
 * `GET /history?hours=…&bucket=…`. Token-ul de acces merge în antetul
 * `Authorization`, ca și la WebSocket — backend-ul validează același JWT.
 */
export async function fetchHistory(
  accessToken: string | null,
  window: HistoryWindow,
  metrics: MetricKey[],
  signal?: AbortSignal,
): Promise<HistoryPoint[]> {
  const base = getRuntimeConfig().apiBaseUrl.replace(/\/$/, '');
  const query = windowQuery(window, metrics);
  const res = await fetch(`${base}/history?${query}&bucket=${window.bucket}`, {
    credentials: 'include',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
    signal,
  });
  if (!res.ok) throw new Error('Istoricul nu a putut fi încărcat.');

  const body: unknown = await res.json();
  if (!Array.isArray(body)) throw new Error('Istoric în format neașteptat.');
  return body.filter(isHistoryPoint);
}

/**
 * Fereastra se trimite ca `from`/`to`, nu ca `hours`: după o selecție pe grafic
 * intervalul nu se mai termină „acum”, iar `hours` n-ar putea să-l exprime.
 */
function windowQuery(window: HistoryWindow, metrics: MetricKey[]): string {
  const params = new URLSearchParams({
    from: new Date(window.from).toISOString(),
    to: new Date(window.to).toISOString(),
  });
  if (metrics.length > 0) params.set('metrics', metrics.join(','));
  return params.toString();
}

/**
 * `GET /history/summary`. Minimul și maximul vin din citirile brute, nu din
 * serie: media unui interval netezește vârfurile, deci un maxim citit din
 * grafic ar fi mai mic decât cel real.
 */
export async function fetchSummary(
  accessToken: string | null,
  window: HistoryWindow,
  metrics: MetricKey[],
  signal?: AbortSignal,
): Promise<HistorySummary> {
  const base = getRuntimeConfig().apiBaseUrl.replace(/\/$/, '');
  const res = await fetch(`${base}/history/summary?${windowQuery(window, metrics)}`, {
    credentials: 'include',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
    signal,
  });
  if (!res.ok) throw new Error('Statisticile nu au putut fi încărcate.');

  const body: unknown = await res.json();
  if (typeof body !== 'object' || body === null) {
    throw new Error('Statistici în format neașteptat.');
  }
  return body as HistorySummary;
}

/** Aceleași statistici, calculate local în modul demo. */
export function summarizeLocally(points: HistoryPoint[]): HistorySummary {
  const metrics: Partial<Record<MetricKey, MetricSummary>> = {};

  for (const key of METRIC_KEYS) {
    const values = points
      .map((point) => point[key])
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

    metrics[key] =
      values.length === 0
        ? { min: null, max: null, avg: null, last: null, count: 0 }
        : {
            min: Math.min(...values),
            max: Math.max(...values),
            avg: values.reduce((sum, value) => sum + value, 0) / values.length,
            last: values[values.length - 1],
            count: values.length,
          };
  }

  return {
    from: points[0]?.t ?? new Date().toISOString(),
    to: points[points.length - 1]?.t ?? new Date().toISOString(),
    readings: points.length,
    metrics,
  };
}

/** Serie simulată pentru modul demo: ciclu diurn plus o deviație lentă. */
export function createMockHistory(_now: Date, window: HistoryWindow): HistoryPoint[] {
  const spanMs = Math.max(1, window.to - window.from);
  const count = spanMs <= 24 * 3_600_000 ? 48 : spanMs <= 7 * 24 * 3_600_000 ? 84 : 90;
  const stepMs = spanMs / (count - 1);
  const points: HistoryPoint[] = [];

  for (let i = count - 1; i >= 0; i--) {
    const at = new Date(window.to - i * stepMs);
    const hour = at.getHours() + at.getMinutes() / 60;
    const daylight = Math.max(0, Math.sin(((hour - 6) / 12) * Math.PI));
    // Deviație sezonieră lentă, ca intervalele lungi să nu arate perfect ciclic.
    const drift = Math.sin(i / 11) * 2.5;

    points.push({
      t: at.toISOString(),
      temperature: Math.round((12 + daylight * 16 + drift + (Math.random() - 0.5)) * 10) / 10,
      humidity: Math.round(Math.max(20, Math.min(100, 78 - daylight * 30 - drift))),
      pressure: Math.round((1013 + Math.sin(i / 9) * 6 + (Math.random() - 0.5)) * 10) / 10,
      wind_speed: Math.round(6 + daylight * 9 + Math.random() * 5),
      wind_direction: Math.round((180 + Math.sin(i / 7) * 170 + 360) % 360),
      precipitation: i % 11 === 0 ? Math.round(Math.random() * 30) / 10 : 0,
      uv_index: Math.round(daylight * 8 * 10) / 10,
      illuminance: Math.round(daylight * 82_000),
      battery: Math.round(Math.max(0, 100 - i * 0.03)),
    });
  }

  return points;
}
